import { useFormContext } from 'react-hook-form';
import { useCreatives } from '../api';
import type { CreativeEntry, PackEntry } from 'types/shared';
import { queryClient } from 'lib/react-query';
import queryKeys from '../api/queryKeys';
import { useEffect, useMemo, useState } from 'react';

const isActivePackIndex = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const shallowEqualStrings = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const sameCreativeRowsById = (
  a: readonly CreativeEntry[],
  b: readonly CreativeEntry[],
): boolean =>
  a.length === b.length && a.every((row, i) => row?.id === b[i]?.id);

interface Params {
  ad_type?: string;
  network?: string;
  forceEnabled?: boolean;
  with_hypothesis?: boolean;
}

interface UseRowsCreativesReturn {
  rows?: CreativeEntry[];
  selectedRows?: CreativeEntry[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  error?: Record<string, any>;
}

const useRowsCreatives =
  ({
     params,
   }: { params?: Params } = {}): UseRowsCreativesReturn => {
    const { watch, setValue } = useFormContext();
    const app = watch('app') as string;
    const network = watch('network') as string;
    const selectedCreatives = watch('selectedCreatives') as string[];
    const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];
    const activePacksWithCreatives = watch('activePacksWithCreatives') as
      | number
      | null;
    const packAdType = watch(
      `packsWithCreatives.${activePacksWithCreatives}.adType`,
    ) as string;
    const adType = packAdType || (watch('adType') as string);
    const filterCreativeByName = watch(
      `packsWithCreatives.${activePacksWithCreatives}.filterCreativeByName`,
    ) as string[];

    const {
      data: rows,
      isLoading,
      isSuccess,
      isError,
      error,
    } = useCreatives({
      config: {
        enabled: params?.forceEnabled ?? !!adType,
      },
      params: {
        app,
        query: filterCreativeByName ?? '',
        ...(params?.ad_type ?? adType ? { ad_type: params?.ad_type ?? adType } : {}),
        network: params?.network ?? network,
        ...(params?.with_hypothesis ? { with_hypothesis: true } : {}),
      },
    });

    const cachedCreativesData = queryClient
      .getQueryCache()
      .findAll({ queryKey: queryKeys.allCreatives })
      .flatMap((query) => query.state.data as CreativeEntry[])
      .filter(Boolean); // Filter out undefined/null values

    const packIds = useMemo(() => {
      if (
        isActivePackIndex(activePacksWithCreatives) &&
        packsWithCreatives?.[activePacksWithCreatives]?.creatives
      ) {
        return packsWithCreatives[activePacksWithCreatives].creatives as string[];
      }
      return [];
    }, [activePacksWithCreatives, packsWithCreatives]);

    const list = useMemo(
      () => [...new Set([...(packIds ?? []), ...(selectedCreatives ?? [])])],
      [packIds, selectedCreatives],
    );

    const selectedRows = list
      .map((id) => cachedCreativesData.find((c) => c && c?.id === id))
      .filter(Boolean) as CreativeEntry[]; // Filter out undefined values

    const [stateRows, setStateRows] = useState<CreativeEntry[]>([]);

    useEffect(() => {
      const filterById = (item: CreativeEntry) =>
        !rows?.some((c) => c && item && c?.id === item?.id);

      let actualRows: CreativeEntry[];
      if (activePacksWithCreatives !== null) {
        const findByIdInCache = (id: string) =>
          cachedCreativesData.find((c) => c && c?.id === id);
        const selectedPackRows = packIds
          ?.map(findByIdInCache)
          .filter(Boolean) as CreativeEntry[]; // Filter out undefined values
        const keepUniqSelectedRows = selectedPackRows.filter(filterById);
        actualRows = [...keepUniqSelectedRows, ...(rows ?? [])];

        // Synchronize selectedCreatives with packIds when the pack grid owns selection.
        // Skip for Facebook hypothesis testing: `CreativesGrid` stays mounted (hidden)
        // while `TestCreativesList` manages `selectedCreatives` for multi-hypothesis staging.
        if (!params?.with_hypothesis && !shallowEqualStrings(selectedCreatives, packIds ?? [])) {
          setValue('selectedCreatives', packIds ?? []);
        }
      } else {
        const keepUniqSelectedRows = selectedRows.filter(filterById);
        actualRows = [...keepUniqSelectedRows, ...(rows ?? [])];
      }

      // Final safety check to remove any undefined values
      const validRows = actualRows.filter(Boolean);

      if (!sameCreativeRowsById(stateRows, validRows)) {
        setStateRows(validRows);
      }
    }, [
      activePacksWithCreatives,
      packIds,
      params?.with_hypothesis,
      rows,
      selectedRows,
      selectedCreatives,
      setValue,
      cachedCreativesData,
      stateRows,
    ]);

    useEffect(() => {
      if (isLoading && stateRows.length) setStateRows([]);
    }, [isLoading, stateRows]);

    return {
      rows: stateRows,
      selectedRows,
      isLoading,
      isSuccess,
      isError,
      error: error ?? {},
    };
  };

export default useRowsCreatives;